/**
 * The manual down payment (in dollars) expressed as the LTV the backend consumes.
 *
 * The backend has exactly ONE lever for purchase leverage:
 *
 *     purchase_loan_amount  = purchase_price * (purchase_ltv / 100)
 *     purchase_cash_required = purchase_price - purchase_loan_amount
 *
 * There is no field for a dollar down payment. A UI that offers one must convert it,
 * or the control is decorative — which is exactly what it was: typing $5,000 moved two
 * display strings and fired zero recalculations, while the loan, the interest, the
 * points, Total Cash In and Cash-on-Cash all went on using the untouched LTV.
 *
 * The arithmetic is one line. What makes this a module is the boundaries, where the
 * arithmetic alone answers WRONG and the right answer has to be chosen:
 *
 *   - blank field         -> keep the LTV. Flipping the "$ Amount" toggle is a statement
 *                            about how the user wants to TYPE the number, not a statement
 *                            that the number is zero. Reading blank as $0 would silently
 *                            turn the deal into 100% financing nobody asked for.
 *   - an explicit $0      -> LTV 100. Zero down IS a real structure (seller carry, 100%
 *                            hard money), so it must be reachable — and it must be
 *                            distinguishable from blank, which is why the input stores 0
 *                            with `??` rather than collapsing it to '' with `||`.
 *   - price <= 0 / absent -> keep the LTV. A dollar amount has no ratio without a price,
 *                            and dividing anyway yields Infinity, which reaches the
 *                            backend as a loan of Infinity.
 *   - down >= price       -> LTV 0. You cannot put down more than the house costs. The
 *                            naive result is a NEGATIVE LTV, i.e. a negative loan, which
 *                            the backend would happily turn into negative interest and a
 *                            cash requirement larger than the purchase price.
 *   - down < 0            -> LTV 100, same as $0. A negative down payment is not a loan
 *                            top-up; it is a typo.
 *
 * The value is deliberately NOT rounded. Rounding the LTV to 2 decimals moves the
 * implied down payment by up to ~$4 on a $89k purchase, which is precisely the kind of
 * "close enough" that makes a breakdown stop summing to its own total.
 */

const isPositiveFinite = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0;

/**
 * @param {object}  args
 * @param {string}  args.mode              'ltv' | 'manual' — which input the user is driving
 * @param {number|string|null} args.manualDownPayment  the typed dollar amount ('' when blank)
 * @param {number}  args.purchasePrice     the SAME price the request carries, so the
 *                                         backend divides by what we divided by
 * @param {number|string} args.ltv         the LTV field, returned untouched in every
 *                                         case where the manual amount cannot decide
 * @returns {number} the purchase_ltv to send
 */
export function impliedPurchaseLtv({ mode, manualDownPayment, purchasePrice, ltv }) {
  const ltvFallback = parseFloat(ltv);
  const fallback = Number.isFinite(ltvFallback) ? ltvFallback : 80;

  if (mode !== 'manual') return fallback;

  const price = typeof purchasePrice === 'string' ? parseFloat(purchasePrice) : purchasePrice;
  if (!isPositiveFinite(price)) return fallback;

  // '' | null | undefined are "nothing typed yet", NOT zero. Number('') === 0, so the
  // emptiness has to be caught before the coercion, not after it.
  if (manualDownPayment === '' || manualDownPayment === null || manualDownPayment === undefined) {
    return fallback;
  }
  const down = typeof manualDownPayment === 'string'
    ? parseFloat(manualDownPayment.replace(/[$,\s]/g, ''))
    : manualDownPayment;
  if (!Number.isFinite(down)) return fallback;

  if (down <= 0) return 100;      // nothing down => the loan is the whole price
  if (down >= price) return 0;    // all of it down => no loan at all

  return (1 - down / price) * 100;
}

/**
 * What the backend will actually treat as the down payment, given that LTV. Shown under
 * the input so a clamped or blank entry is VISIBLE instead of silently disagreeing with
 * the number the user typed.
 */
export function effectiveDownPayment(purchasePrice, ltvToSend) {
  const price = typeof purchasePrice === 'string' ? parseFloat(purchasePrice) : purchasePrice;
  if (!isPositiveFinite(price)) return 0;
  const l = parseFloat(ltvToSend);
  if (!Number.isFinite(l)) return 0;
  return price - price * (l / 100);
}
